import { forwardRef } from 'react';
import type { HTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

type BadgeVariant = 'success' | 'warning' | 'danger' | 'neutral' | 'info';
type BadgeSize = 'sm' | 'md';

// FASE-B (design system): cores das variantes agora vêm dos tokens AA da
// maquete (success 5.60:1, info 7.49:1 sobre o tint claro; warning re-escurecido
// na auditoria 13/08 — ≥4.9:1 sobre o próprio tint no pior fundo, trava em
// badge-contraste.test.ts).
// `info` deixou de ser vermelho-diluído — azul-petróleo warm, pra "Recorrente"
// e afins não competirem com o vermelho de ação. No dark, texto clareado.
const VARIANT_CLASSES: Record<BadgeVariant, { bg: string; text: string; dot: string }> = {
  success: {
    bg: 'bg-[rgba(63,104,50,0.12)] dark:bg-[rgba(63,104,50,0.30)]',
    text: 'text-success dark:text-[#9FC791]',
    dot: 'bg-success dark:bg-[#9FC791]',
  },
  warning: {
    bg: 'bg-[rgba(119,84,32,0.13)] dark:bg-[rgba(119,84,32,0.30)]',
    text: 'text-warning dark:text-[#D9AE6B]',
    dot: 'bg-warning dark:bg-[#D9AE6B]',
  },
  danger: {
    bg: 'bg-red-soft dark:bg-red-soft-dark',
    text: 'text-red-deep dark:text-red-glow',
    dot: 'bg-red',
  },
  neutral: {
    bg: 'bg-paper-warm dark:bg-paper-dark-warm',
    text: 'text-ink-2 dark:text-ink-dark-2',
    dot: 'bg-ink-3 dark:bg-ink-dark-3',
  },
  info: {
    bg: 'bg-[rgba(35,78,107,0.10)] dark:bg-[rgba(35,78,107,0.35)]',
    text: 'text-info dark:text-[#9CC2DC]',
    dot: 'bg-info dark:bg-[#9CC2DC]',
  },
};

// FASE-B: piso tipográfico 12px — o sm de 10px foi extinto (dor 2 do
// diagnóstico). `sm` continua aceito pra não quebrar chamadas, só encolhe
// o padding.
const SIZE_CLASSES: Record<BadgeSize, string> = {
  sm: 'text-xs px-2 py-0.5 gap-1',
  md: 'text-xs px-2.5 py-1 gap-1.5',
};

interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  size?: BadgeSize;
  /** Mostra o pontinho colorido à esquerda do texto */
  dot?: boolean;
  /**
   * 🔴 A cor do PONTO, quando ela precisa ser outra que a do contêiner (T2,
   * 29/09/2026).
   *
   * **O caso:** *"Propostas sem ação"* e *"Propostas em atraso"* são dois
   * números IRMÃOS, lado a lado, e apareciam com fundos diferentes — um
   * avermelhado, outro amarelado. ⚠️ **Dois números irmãos com aparências
   * diferentes parecem coisas diferentes**, e alguém conclui que um é aviso e
   * o outro é botão.
   *
   * ✅ Decisão do Matheus, olhando a tela: **mesma borda, mesmo formato, mesmo
   * tamanho — a ÚNICA diferença é a cor da bolinha.**
   * ⚠️ Isto NÃO cria um segundo Badge: o contêiner continua saindo de
   * `variant`, e só o ponto passa a ter voz própria.
   */
  corDoPonto?: BadgeVariant;
}

/**
 * Badge — pílulas pequenas pra status, contagens, tags.
 *
 * Variants: success, warning, danger, neutral, info.
 * Sizes: sm, md (default).
 *
 * <Badge variant="success" dot>Ganho</Badge>
 * <Badge variant="warning">Em aberto</Badge>
 * <Badge variant="danger" dot>Urgente</Badge>
 */
export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ variant = 'neutral', size = 'md', dot, corDoPonto, className, children, ...props }, ref) => {
    const v = VARIANT_CLASSES[variant];
    return (
      <span
        ref={ref}
        className={cn(
          'inline-flex items-center rounded-pill font-semibold',
          v.bg,
          v.text,
          SIZE_CLASSES[size],
          className
        )}
        {...props}
      >
        {dot && (
          <span
            className={cn('w-1.5 h-1.5 rounded-full', (corDoPonto ? VARIANT_CLASSES[corDoPonto] : v).dot)}
            aria-hidden="true"
          />
        )}
        {children}
      </span>
    );
  }
);
Badge.displayName = 'Badge';
